
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { AuditService } from '@/services/auditService';
import { UserRole } from './useCurrentUserRole';

export const useUserRoleMutations = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Update user role mutation
  const updateUserRoleMutation = useMutation({
    mutationFn: async ({ userId, newRole }: { userId: string; newRole: UserRole }) => {
      const { data, error } = await supabase.functions.invoke('update-user-role', {
        body: { user_id: userId, role: newRole }
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || 'Failed to update role');
      }
    },
    onSuccess: async (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
      
      // Log the role change
      await AuditService.logRoleChange(
        variables.userId,
        'previous_role', // This would need to be tracked in the mutation
        variables.newRole,
        variables.userId
      );
      
      toast({
        title: "Role updated",
        description: "User role has been successfully updated."
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating role",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Update user profile mutation
  const updateUserProfileMutation = useMutation({
    mutationFn: async ({ userId, first_name, last_name, email, mobile_number, time_zone }: { 
      userId: string; 
      first_name: string; 
      last_name: string; 
      email: string; 
      mobile_number?: string; 
      time_zone?: string; 
    }) => {
      const { error } = await supabase
        .from('profiles')
        .update({
          first_name,
          last_name,
          full_name: `${first_name} ${last_name}`, // Keep full_name updated for backward compatibility
          email,
          mobile_number,
          time_zone,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usersWithRoles'] });
    }
  });

  return {
    updateUserRole: updateUserRoleMutation.mutate,
    isUpdating: updateUserRoleMutation.isPending,
    updateUserProfile: updateUserProfileMutation.mutate,
  };
};
